<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();
// Humans only (no "None", no bots): a transfer targets an agent or a team.
const { agentsList } = useAgentsList(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const teams = computed(() => store.getters['teams/getTeams']);

const [showDropdown, toggleDropdown] = useToggle(false);

const assignedAgentId = computed(() => currentChat.value?.meta?.assignee?.id);
const assignedTeamId = computed(() => currentChat.value?.meta?.team?.id);

const menuSections = computed(() =>
  [
    {
      title: t('CONVERSATION.HEADER.TRANSFER_AGENTS'),
      items: (agentsList.value || []).map(agent => ({
        label: agent.name,
        value: `agent-${agent.id}`,
        action: 'assign_agent',
        thumbnail: { name: agent.name, src: agent.thumbnail },
        isSelected: agent.id === assignedAgentId.value,
        agent,
      })),
    },
    {
      title: t('CONVERSATION.HEADER.TRANSFER_TEAMS'),
      items: (teams.value || []).map(team => ({
        label: team.name,
        value: `team-${team.id}`,
        action: 'assign_team',
        icon: 'i-lucide-users',
        isSelected: team.id === assignedTeamId.value,
        team,
      })),
    },
  ].filter(section => section.items.length > 0)
);

const transferToAgent = agent => {
  const conversationId = props.conversationId;
  store.dispatch('setCurrentChatAssignee', {
    conversationId,
    assignee: agent,
    assigneeType: 'User',
  });
  store
    .dispatch('assignAgent', { conversationId, agentId: agent.id, assigneeType: 'User' })
    .then(() => useAlert(t('CONVERSATION.CHANGE_AGENT')));
};

const transferToTeam = team => {
  const conversationId = props.conversationId;
  store.dispatch('setCurrentChatTeam', { team, conversationId });
  store
    .dispatch('assignTeam', { conversationId, teamId: team.id })
    .then(() => useAlert(t('CONVERSATION.CHANGE_TEAM')));
  // Handing the conversation to a team drops the current agent, so it lands in
  // the team's queue instead of staying with whoever had it.
  store.dispatch('setCurrentChatAssignee', {
    conversationId,
    assignee: null,
    assigneeType: null,
  });
  store.dispatch('assignAgent', {
    conversationId,
    agentId: null,
    assigneeType: null,
  });
};

const handleAction = ({ action, agent, team }) => {
  toggleDropdown(false);
  if (action === 'assign_agent') transferToAgent(agent);
  else if (action === 'assign_team') transferToTeam(team);
};
</script>

<template>
  <div
    v-on-clickaway="() => toggleDropdown(false)"
    class="relative flex items-center"
  >
    <Button
      v-tooltip="t('CONVERSATION.HEADER.TRANSFER_TOOLTIP')"
      :label="t('CONVERSATION.HEADER.TRANSFER')"
      icon="i-lucide-arrow-right-left"
      size="sm"
      variant="ghost"
      color="slate"
      class="rounded-md"
      @click="toggleDropdown()"
    />
    <DropdownMenu
      v-if="showDropdown"
      :menu-sections="menuSections"
      show-search
      :search-placeholder="t('CONVERSATION.HEADER.TRANSFER_SEARCH')"
      class="mt-1 ltr:right-0 rtl:left-0 top-full max-h-96 w-64"
      @action="handleAction"
    />
  </div>
</template>
